import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ProductionCalendarModal from '../ProductionCalendarModal';
import { apiService } from '../../services/api';
import './Navbar.css';

const OWNER_BADGE_PREF_KEY = 'aot_admin_show_owners_v1';
const DJANGO_ADMIN_URL = process.env.NODE_ENV === 'production'
  ? '/admin/'
  : 'http://127.0.0.1:8000/admin/';

const readShowOwnersPref = () => {
  try {
    const raw = localStorage.getItem(OWNER_BADGE_PREF_KEY);
    if (raw === null) {
      return true;
    }
    return raw === '1';
  } catch (_) {
    return true;
  }
};

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isAdmin = !!user?.is_staff || !!user?.is_superuser || String(user?.role || '').toLowerCase() === 'admin';
  const [isDataDropdownOpen, setIsDataDropdownOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [showOwners, setShowOwners] = useState(readShowOwnersPref);
  const [reminderSummary, setReminderSummary] = useState({ upcoming_count: 0, due_count: 0, unread_notifications: 0 });
  const [supportSummary, setSupportSummary] = useState({ unread_requests: 0, new_requests: 0, open_requests: 0 });
  const currentDay = new Date().getDate();

  const loadSummary = useCallback(async () => {
      if (!user) return;
      try {
        const resp = await apiService.getRemindersSummary();
        setReminderSummary(resp.data || { upcoming_count: 0, due_count: 0, unread_notifications: 0 });
      } catch (_) {
        // ignore
      }
      try {
        const supportResp = await apiService.getSupportSummary();
        setSupportSummary(supportResp.data || { unread_requests: 0, new_requests: 0, open_requests: 0 });
      } catch (_) {
        // ignore
      }
  }, [user]);

  useEffect(() => {
    let timer = null;
    loadSummary();
    timer = setInterval(loadSummary, 60000);
    window.addEventListener('aot-support-updated', loadSummary);
    return () => {
      if (timer) clearInterval(timer);
      window.removeEventListener('aot-support-updated', loadSummary);
    };
  }, [loadSummary]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/', label: 'Главная' },
    { path: '/auctions', label: 'Торги' },
  ];

  const dataItems = [
    { path: '/debtors', label: 'Должники' },
    { path: '/organizers', label: 'Организаторы' },
    { path: '/managers', label: 'Управляющие' },
    ...(isAdmin ? [{ path: '/sros', label: 'СРО' }] : []),
    ...(isAdmin ? [{ path: '/etps', label: 'ЭТП' }] : []),
    ...(isAdmin ? [{ path: '/courts', label: 'Суды' }] : []),
    ...(isAdmin ? [{ path: '/classifiers', label: 'Классификаторы' }] : []),
    { path: '/creditors', label: 'Кредиторы' },
    ...(isAdmin ? [{ path: '/lots', label: 'Лоты' }] : []),
    { path: '/accounts', label: 'Банковские счета' },
    { path: '/documents', label: 'Шаблоны документов' },
    ...(isAdmin ? [{ path: '/users', label: 'Пользователи' }] : []),
    ...(isAdmin ? [{ path: '/support', label: 'Техподдержка' }] : []),
  ];

  return (
    <nav className="navbar">
      <div className="navbar-brand">
        <span className="navbar-brand-mark" aria-hidden="true">AOT</span>
        <Link to="/">
          <span className="navbar-brand-title">Автоматизированный организатор торгов</span>
        </Link>
      </div>
      <ul className="navbar-nav">
        {navItems.map((item) => (
          <li key={item.path} className="nav-item">
            <Link
              to={item.path}
              className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
              onClick={(e) => {
                // Если мы уже на странице торгов, отправляем событие для сброса состояния
                if (item.path === '/auctions' && location.pathname === '/auctions') {
                  e.preventDefault();
                  // Отправляем кастомное событие для сброса состояния торгов
                  window.dispatchEvent(new CustomEvent('resetAuctionsState'));
                }
              }}
            >
              {item.label}
            </Link>
          </li>
        ))}
        <li 
          className="nav-item dropdown"
          onMouseEnter={() => setIsDataDropdownOpen(true)}
          onMouseLeave={() => setIsDataDropdownOpen(false)}
        >
          <span className={`nav-link dropdown-toggle ${dataItems.some(item => location.pathname === item.path) ? 'active' : ''}`}>
            Данные
          </span>
          <ul className={`dropdown-menu ${isDataDropdownOpen ? 'show' : ''}`}>
            {dataItems.map((item) => (
              <li key={item.path} className="dropdown-item">
                <Link
                  to={item.path}
                  className={`dropdown-link ${location.pathname === item.path ? 'active' : ''}`}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </li>
        <li className="nav-item">
          <Link
            to="/knowledge-base"
            className={`nav-link ${location.pathname === '/knowledge-base' ? 'active' : ''}`}
          >
            База знаний
          </Link>
        </li>
      </ul>
      <div className="navbar-actions">
        {!!isAdmin && (
          <label className="owner-toggle" title="Показывать владельцев объектов (owner:email)">
            <span className="owner-toggle-label">owner</span>
            <span className="switch">
              <input
                type="checkbox"
                checked={showOwners}
                onChange={(e) => {
                  const next = e.target.checked;
                  setShowOwners(next);
                  try {
                    localStorage.setItem(OWNER_BADGE_PREF_KEY, next ? '1' : '0');
                  } catch (_) {}
                  window.dispatchEvent(new Event('aot-owner-badge-pref'));
                }}
              />
              <span className="slider" />
            </span>
          </label>
        )}
        <button
          type="button"
          className="reminder-bell support-nav-button"
          title={isAdmin ? "Обращения в техподдержку" : "Мои обращения"}
          onClick={() => navigate('/support')}
        >
          <span className="navbar-action-glyph navbar-action-glyph--question" aria-hidden="true">?</span>
          {supportSummary.unread_requests > 0 && (
            <span className="notification-badge">{supportSummary.unread_requests}</span>
          )}
        </button>
        <button
          type="button"
          className="reminder-bell"
          title="Напоминания"
          onClick={() => navigate('/reminders')}
        >
          <svg className="navbar-action-glyph navbar-action-glyph--bell" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
            <path d="M10 21h4" />
          </svg>
          {reminderSummary.upcoming_count > 0 && (
            <span className="notification-badge">{reminderSummary.upcoming_count}</span>
          )}
        </button>
        <button 
          className="calendar-button" 
          onClick={() => setIsCalendarOpen(true)}
          title="Производственный календарь"
        >
          <div className="calendar-icon">
            <div className="calendar-icon-header"></div>
            <div className="calendar-icon-body">
              <span className="calendar-icon-day">{currentDay}</span>
            </div>
          </div>
        </button>
        <div className="user-menu">
          <button
            type="button"
            className="user-name-button"
            onClick={() => navigate('/profile')}
            title="Редактировать профиль"
          >
            Привет, {user?.full_name || 'пользователь'}!
          </button>
          {!!isAdmin && (
            <a href={DJANGO_ADMIN_URL} className="admin-link" target="_blank" rel="noopener noreferrer">
              Django Admin
            </a>
          )}
          <button
            type="button"
            onClick={handleLogout}
            className="logout-icon-button"
            title="Выйти"
            aria-label="Выйти"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M10 17l5-5-5-5" />
              <path d="M15 12H6" />
              <path d="M14 4h4v16h-4" />
            </svg>
          </button>
        </div>
      </div>
      <ProductionCalendarModal 
        isOpen={isCalendarOpen} 
        onClose={() => setIsCalendarOpen(false)} 
      />
    </nav>
  );
};

export default Navbar;
